// Finds windows in the uploaded floor-plan image along the plan's outer walls.
// Broker plans draw walls as solid dark bands; a window is a light gap in the
// band crossed by thin glazing lines, while a door opening is a fully light
// gap. So we walk each outer wall, look across its thickness at every step,
// and classify: mostly dark = wall, light with a few dark lines = window,
// all light = opening (door) — only the window runs are returned.
import { Wall, WindowOpening, detectRooms, newId, PLAN_W, PLAN_H } from "./home";

const RES = 2; // canvas pixels per design unit (glazing lines are thin)

const inPoly = (x: number, y: number, pts: { x: number; y: number }[]) => {
  let r = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i++) {
    const a = pts[i], b = pts[j];
    if (a.y > y !== b.y > y && x < ((b.x - a.x) * (y - a.y)) / (b.y - a.y) + a.x) r = !r;
  }
  return r;
};

// Draw the background exactly as the SVG does: fitted ("meet") into the
// design space and rotated about its centre by planRot
const rasterize = (url: string, rot: number): Promise<ImageData> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const c = document.createElement("canvas");
      c.width = PLAN_W * RES;
      c.height = PLAN_H * RES;
      const g = c.getContext("2d")!;
      g.fillStyle = "#fff";
      g.fillRect(0, 0, c.width, c.height);
      const k = Math.min(PLAN_W / img.width, PLAN_H / img.height);
      const dw = img.width * k, dh = img.height * k;
      g.scale(RES, RES);
      g.translate(PLAN_W / 2, PLAN_H / 2);
      g.rotate((rot * Math.PI) / 180);
      g.translate(-PLAN_W / 2, -PLAN_H / 2);
      g.drawImage(img, (PLAN_W - dw) / 2, (PLAN_H - dh) / 2, dw, dh);
      resolve(g.getImageData(0, 0, c.width, c.height));
    };
    img.onerror = () => reject(new Error("Kunde inte läsa bakgrundsbilden."));
    img.src = url;
  });

export async function detectWindows(
  planUrl: string,
  planRot: number,
  walls: Wall[],
  existing: WindowOpening[],
  scale: number,
): Promise<WindowOpening[]> {
  const data = await rasterize(planUrl, planRot);
  const dark = (x: number, y: number) => {
    const px = Math.round(x * RES), py = Math.round(y * RES);
    if (px < 0 || py < 0 || px >= data.width || py >= data.height) return false;
    const i = (py * data.width + px) * 4;
    return 0.299 * data.data[i] + 0.587 * data.data[i + 1] + 0.114 * data.data[i + 2] < 150;
  };

  const indoor = detectRooms(walls).filter((r) => !r.balcony);
  const isIndoors = (x: number, y: number) => indoor.some((r) => inPoly(x, y, r.points));
  const minLen = scale > 0 ? 0.45 * scale : 20; // shorter light gaps are noise
  const found: WindowOpening[] = [];

  for (const w of walls) {
    const len = Math.hypot(w.x2 - w.x1, w.y2 - w.y1);
    if (w.door || w.balcony || len < minLen) continue;
    const ux = (w.x2 - w.x1) / len, uy = (w.y2 - w.y1) / len;
    const nx = -uy, ny = ux;
    // Only outer walls: indoors on exactly one side
    const mx = (w.x1 + w.x2) / 2, my = (w.y1 + w.y2) / 2;
    if (indoor.length && isIndoors(mx + nx * 8, my + ny * 8) === isIndoors(mx - nx * 8, my - ny * 8)) continue;

    // Classify each 1-unit step along the wall (ends skipped: corners)
    const kinds: ("wall" | "win" | "open")[] = [];
    for (let t = 0; t <= len; t += 1) {
      let darkCount = 0, n = 0;
      for (let o = -7; o <= 7; o += 0.5) {
        n++;
        if (dark(w.x1 + ux * t + nx * o, w.y1 + uy * t + ny * o)) darkCount++;
      }
      const f = darkCount / n;
      kinds.push(f > 0.55 ? "wall" : f > 0 ? "win" : "open");
    }
    // Close 1–2 step blips so a run isn't split by a stray pixel
    for (let i = 1; i < kinds.length - 1; i++) {
      if (kinds[i] !== kinds[i - 1] && kinds[i - 1] === kinds[i + 1]) kinds[i] = kinds[i - 1];
    }
    let i = 0;
    while (i < kinds.length) {
      if (kinds[i] === "wall") { i++; continue; }
      let j = i;
      let winSteps = 0;
      while (j < kinds.length && kinds[j] !== "wall") { if (kinds[j] === "win") winSteps++; j++; }
      const a = i, b = j - 1;
      // A real window: long enough, not touching the wall's ends, and mostly
      // glazing (a door gap is almost all "open")
      if (b - a >= minLen && a > 2 && b < kinds.length - 3 && winSteps / (b - a + 1) > 0.6) {
        const x1 = w.x1 + ux * a, y1 = w.y1 + uy * a, x2 = w.x1 + ux * b, y2 = w.y1 + uy * b;
        const overlaps = [...existing, ...found].some((e) => {
          const ea = (e.x1 - w.x1) * ux + (e.y1 - w.y1) * uy, eb = (e.x2 - w.x1) * ux + (e.y2 - w.y1) * uy;
          const off = Math.abs((e.x1 - w.x1) * nx + (e.y1 - w.y1) * ny);
          return off < 4 && Math.max(ea, eb) > a && Math.min(ea, eb) < b;
        });
        if (!overlaps) found.push({ id: newId(), x1: Math.round(x1), y1: Math.round(y1), x2: Math.round(x2), y2: Math.round(y2) });
      }
      i = j;
    }
  }
  return found;
}
